import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import {
  F0Box,
  F0Dialog,
  F0DialogPrimaryAction,
  F0DialogPrimaryActionItem,
  F0DialogSecondaryAction,
} from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { TaskActivityDocument, TaskActivityQuery } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useActivity } from 'modules/core/lib/activities/activity'
import { ActivityComponent } from 'modules/core/lib/activities/types'
import buildUrl from 'modules/core/lib/buildUrl'
import { getEmployeeDisplayName } from 'modules/core/lib/employeeName'

import {
  PossibleTaskState,
  TaskActionsHookResult,
  TaskDefinition,
} from 'modules/inbox_tasks/lib/abstraction'
import { trackCloseTask, trackGoToTaskDetail } from 'modules/inbox_tasks/lib/events'
import {
  getTaskTitle,
  getTaskableForTypename,
  useTaskDefinition,
} from 'modules/inbox_tasks/lib/helpers'

import Content from './Content'
import { useTaskActions } from './Footer'
import { getMetadata, getModule, getStatusTag, useModuleHref } from './utils'

type Data = {
  id: string
}

const CLOSE_TIMEOUT = 100

type DialogActions = {
  primaryAction?: F0DialogPrimaryAction | F0DialogPrimaryActionItem[]
  secondaryAction?: F0DialogSecondaryAction
}

const noopUseActions = (): TaskActionsHookResult => ({})

type TaskDialogContentProps = {
  taskDefinition: TaskDefinition
  task: NonNullable<TaskActivityQuery['tasks']['inboxTask']>
  taskId: string
  state?: PossibleTaskState
  updateState: React.Dispatch<React.SetStateAction<PossibleTaskState | undefined>>
  setDialogActions: React.Dispatch<React.SetStateAction<DialogActions>>
}

function TaskDialogContent({
  taskDefinition,
  task,
  taskId,
  state,
  updateState,
  setDialogActions,
}: TaskDialogContentProps) {
  const taskable = task?.taskable
  const kind = task?.kind

  const safeTaskable =
    taskDefinition && taskable ? getTaskableForTypename(taskable, taskDefinition) : undefined

  const useActions = taskDefinition.useActions ?? noopUseActions
  const rawActions = useActions({
    taskId,
    taskable: safeTaskable,
    taskKind: kind ?? undefined,
    state,
    updateState,
    status: task?.status ?? undefined,
  })

  const { primaryDialogAction, secondaryDialogAction } = useTaskActions({
    taskId,
    taskKind: kind ?? undefined,
    status: task?.status ?? undefined,
    ...rawActions,
  })

  useLayoutEffect(() => {
    setDialogActions({
      primaryAction: primaryDialogAction,
      secondaryAction: secondaryDialogAction,
    })
  }, [primaryDialogAction, secondaryDialogAction, setDialogActions])

  useEffect(() => {
    return () => setDialogActions({})
  }, [setDialogActions])

  return null
}

export const TaskActivity: ActivityComponent<Data> = ({ id }) => {
  const [state, setState] = useState<PossibleTaskState | undefined>()
  const [dialogActions, setDialogActions] = useState<DialogActions>({})

  const idChanged = useRef(false)

  useEffect(() => {
    idChanged.current = true
    setTimeout(() => {
      idChanged.current = false
    }, CLOSE_TIMEOUT + 50)
  }, [id])

  const taskId = id

  const { open, closeActivity } = useActivity()

  const { data, loading } = useQuery(TaskActivityDocument, {
    variables: {
      taskId,
    },
    skip: !open,
  })

  const task = data?.tasks.inboxTask

  const kind = task?.kind
  const taskable = task?.taskable
  const affectee = task?.affecteesConnection?.nodes?.[0]?.employee ?? undefined

  const category = task?.category ?? undefined
  const status = task?.status

  useEffect(() => {
    if (status) {
      trackGoToTaskDetail({
        category,
        status,
      })
    }
  }, [category, status])

  const affecteeName = affectee ? getEmployeeDisplayName(affectee) : undefined

  const taskDefinition = useTaskDefinition({
    taskableTypename: taskable?.__typename,
    kind: kind ?? undefined,
  })

  useEffect(() => {
    if (taskDefinition) {
      setState(taskDefinition.initialState)
    }
  }, [taskDefinition])

  const title =
    taskable && affecteeName
      ? getTaskTitle({
          taskName: task.name,
          taskableInfoForTitle: taskable,
          affecteeName,
          dueOn: task.dueOn ?? undefined,
        })
      : undefined

  const createdBy = task?.author
    ? {
        firstName: task.author.preferredName || task.author.firstName,
        lastName: task.author.lastName,
        avatarUrl: buildUrl(task.author.avatar?.url),
      }
    : undefined

  const assignees = task?.assigneesConnection?.nodes
    ?.map((node) => node.employee)
    .filter(Boolean)
    .map((employee) => ({
      firstName: employee.preferredName || employee.firstName,
      lastName: employee.lastName,
      avatarUrl: buildUrl(employee.avatar?.url),
    }))

  const assigneesTotalCount = task?.assigneesConnection?.totalCount ?? undefined

  const metadata = task
    ? getMetadata({
        createdBy,
        createdAt: task.createdAt,
        completedAt: task.completedAt ?? undefined,
        dueOn: task.dueOn ?? undefined,
        assignees,
        assigneesTotalCount,
      })
    : []

  const handleClose = () => {
    trackCloseTask({
      category,
    })
    setTimeout(() => {
      if (!idChanged.current) {
        closeActivity()
      }
    }, CLOSE_TIMEOUT)
  }

  const showLoader = !task && !taskDefinition && loading

  const statusTag = task?.status ? getStatusTag(task.status) : undefined
  const module = getModule(category)

  const moduleHref = useModuleHref(category)

  return (
    <F0Dialog
      isOpen={open}
      onClose={handleClose}
      position="right"
      module={module && moduleHref ? { ...module, href: moduleHref } : undefined}
      primaryAction={dialogActions.primaryAction}
      secondaryAction={dialogActions.secondaryAction}
    >
      {!!task && !!taskDefinition ? (
        <>
          <ResourceHeader title={title ?? task.name} status={statusTag} metadata={metadata} />
          <F0Box paddingY="xl">
            <Content
              affectee={affectee}
              taskDefinition={taskDefinition}
              taskable={taskable}
              taskId={taskId}
              updateState={setState}
              state={state}
              dueOn={task.dueOn ?? undefined}
              assigneesTotalCount={assigneesTotalCount}
            />
          </F0Box>
          <TaskDialogContent
            key={taskDefinition.taskableTypename}
            taskDefinition={taskDefinition}
            task={task}
            taskId={taskId}
            state={state}
            updateState={setState}
            setDialogActions={setDialogActions}
          />
        </>
      ) : (
        showLoader && <Loading />
      )}
    </F0Dialog>
  )
}
